import React, { useEffect } from 'react';
import { Avatar, DatePicker, Form, Input, Modal, Select, Space } from 'antd';
import dayjs from 'dayjs';
import { EventType, EVENT_TYPES, EVENT_TYPE_LABELS, TeamMemberEvent, TeamMemberInsight } from '../types';
import { EventInput, createEvent, updateEvent } from './eventsService';

interface Props {
    open: boolean;
    onClose: () => void;
    onSaved: () => void;
    members: Array<Pick<TeamMemberInsight, 'login' | 'avatarUrl'>>;
    /** When set, the modal is in edit mode for this event. */
    editing: TeamMemberEvent | null;
}

interface FormShape {
    login: string;
    type: EventType;
    date: dayjs.Dayjs;
    note?: string;
}

const EventFormModal: React.FC<Props> = ({ open, onClose, onSaved, members, editing }) => {
    const [form] = Form.useForm<FormShape>();
    const [submitting, setSubmitting] = React.useState(false);

    useEffect(() => {
        if (!open) return;
        if (editing) {
            form.setFieldsValue({
                login: editing.login,
                type: editing.type,
                date: dayjs(editing.date),
                note: editing.note,
            });
        } else {
            form.resetFields();
        }
    }, [open, editing, form]);

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            setSubmitting(true);
            const input: EventInput = {
                login: values.login,
                type: values.type,
                date: values.date.format('YYYY-MM-DD'),
                note: values.note ?? '',
            };
            if (editing) {
                await updateEvent(editing.id, input);
            } else {
                await createEvent(input);
            }
            onSaved();
            onClose();
        } catch (err) {
            // validateFields throws a structured object — only act on real errors.
            if ((err as any)?.errorFields) return;
            // eslint-disable-next-line no-console
            console.error(err);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            open={open}
            title={editing ? 'Edit event' : 'Add event'}
            onCancel={onClose}
            onOk={handleOk}
            confirmLoading={submitting}
            destroyOnClose
        >
            <Form form={form} layout="vertical">
                <Form.Item name="login" label="Member" rules={[{ required: true }]}>
                    <Select
                        showSearch
                        placeholder="Pick an org member"
                        options={members.map((m) => ({
                            value: m.login,
                            label: (
                                <Space>
                                    <Avatar src={m.avatarUrl} size="small">
                                        {m.login.slice(0, 1).toUpperCase()}
                                    </Avatar>
                                    {m.login}
                                </Space>
                            ),
                            search: m.login,
                        }))}
                        filterOption={(input, option) =>
                            (option?.search as string)?.toLowerCase().includes(input.toLowerCase())
                        }
                    />
                </Form.Item>

                <Form.Item name="type" label="Type" rules={[{ required: true }]}>
                    <Select
                        options={EVENT_TYPES.map((t) => ({ value: t, label: EVENT_TYPE_LABELS[t] }))}
                    />
                </Form.Item>

                <Form.Item name="date" label="Date" rules={[{ required: true }]}>
                    <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                </Form.Item>

                <Form.Item name="note" label="Note">
                    <Input.TextArea rows={3} maxLength={1000} showCount placeholder="Optional context" />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default EventFormModal;
